<template>
  <QuoteLoading
    :loading="
      loadingQuote || loadingUser || applicationLoading || !redirectionHandled
    "
    :quote-id="quoteId"
  />
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import getValidRote from '@/utils/get-valid-route';
import getUserFeatures from '@/utils/user-features';
import { getEnv } from 'ecx.saigon.ui.common';
import RouteNames from '@/router/route-names';

export default {
  name: 'QuoteWidget',
  props: {
    quoteId: String
  },
  data() {
    return {
      redirectionHandled: false
    };
  },
  computed: {
    ...mapState({
      routeQuoteId: (state) => state.quote.routeQuoteId,
      loadingQuote: (state) => state.quote.loadingQuote,
      loadingUser: (state) => state.user.loadingUser,
      applicationLoading: (state) => state.application.applicationLoading,
      application: (state) => state.application.application
    }),
    ...mapGetters('application', [
      'customerSelectionIsNew',
      'vehicleCondition',
      'getFirstInvalidRoute',
      'isApplicationComplete'
    ]),
    ...mapGetters('carSearch', ['isNew']),
    ...mapGetters('user', ['userBlocked']),
    userFeatures() {
      return getUserFeatures();
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        const routeQuoteId = this.$router.currentRoute.params.quoteId;
        if (this.routeQuoteId !== routeQuoteId) {
          this.setRouteQuoteId(routeQuoteId);
        }
      }
    }
  },
  methods: {
    ...mapMutations('carSearch', ['setNewUsedSelected']),
    ...mapMutations('quote', ['setRouteQuoteId']),
    ...mapMutations('application', ['setLeaseId']),
    ...mapActions('application', ['getApplication']),
    ...mapActions('user', ['getUser']),
    ...mapActions('quote', ['getQuote']),
    async goToRedirectionRoute(quoteId) {
      return await this.$router.push({
        name: this.$RouteNames.redirection,
        params: {
          ...this.$router.currentRoute.params,
          quoteId
        }
      });
    },
    async goToFurthestRoute() {
      let route = getValidRote();
      if (route !== this.$router.currentRoute.name) {
        await this.$router.push({
          name: route,
          params: { quoteId: this.routeQuoteId },
          ...(route === RouteNames.calculator &&
            this.$router.currentRoute.query.leaseId && {
              query: { leaseId: this.$router.currentRoute.query.leaseId }
            })
        }).catch(() => {});
      }
    }
  },
  async mounted() {
    const env = await getEnv();
    await this.getUser();

    // Prevent user access if they are restricted
    if (this.userBlocked) {
      return (window.location = env.VUE_APP_CREDIT_CHECK_ROUTE);
    }

    if (this.$route.query.leaseId) {
      this.setLeaseId(this.$route.query.leaseId);
    }

    try {
      await this.getApplication();

      // Ensure the local newUsed value matches the customerOptions
      if (this.customerSelectionIsNew !== this.isNew) {
        this.setNewUsedSelected(this.vehicleCondition);
      }

      // Go to the core app if the application is complete
      if (this.isApplicationComplete) {
        return (window.location = env.VUE_APP_CORE_ROUTE);
      }

      const applicationQuoteId = this.application.metaData.quoteId;
      const noQuoteAndHasAccepted = !this.quoteId && applicationQuoteId;

      const quoteToGet = noQuoteAndHasAccepted
        ? applicationQuoteId
        : this.quoteId;

      await this.getQuote(quoteToGet);

      if (noQuoteAndHasAccepted) {
        await this.goToRedirectionRoute(applicationQuoteId);
        await this.goToFurthestRoute();
        this.redirectionHandled = true;
      }
    } catch (e) {
      if (e === this.$Constants.GQL_ERROR_CODES.notFound) {
        if (this.$router.currentRoute.name !== this.$RouteNames.quoteNotFound) {
          this.$router.push({
            name: this.$RouteNames.quoteNotFound,
            params: { quoteId: this.quoteId }
          }).catch(() => {});
        }
        this.redirectionHandled = true;
      }
    }

    if (!this.redirectionHandled) {
      await this.goToFurthestRoute();
      this.redirectionHandled = true;
    }
  },
  components: {
    QuoteLoading: () => import('../../components/page/quote-loading')
  }
};
</script>
